import React, { useEffect, useRef } from 'react';
import { MessageSquare, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import CommentItem from './CommentItem';
import { useUser } from '../../contexts/userContext';

/**
 * CommentsList - Scrollable list of comments in chat-style format
 * @param {Array} comments - Array of comment objects
 * @param {boolean} isLoading - Whether comments are being loaded
 * @param {string} error - Error message if loading failed
 */
const CommentsList = ({ comments = [], isLoading = false, error = null }) => {
  const { t } = useTranslation();
  const { userDetails } = useUser();
  const scrollContainerRef = useRef(null);
  const shouldAutoScrollRef = useRef(true);

  // Auto-scroll to bottom when new comments are added
  useEffect(() => {
    if (scrollContainerRef.current && shouldAutoScrollRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
    }
  }, [comments]);

  // Track scroll position to determine if user has scrolled up
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
      // Auto-scroll if user is near bottom (within 100px)
      shouldAutoScrollRef.current = scrollHeight - scrollTop - clientHeight < 100;
    }
  };

  // Check if comment belongs to current user
  const isCurrentUserComment = (comment) => {
    if (!userDetails || !comment.added_by_name) return false;
    // Compare with current user's name
    const currentUserName = userDetails.firstName && userDetails.lastName
      ? `${userDetails.firstName} ${userDetails.lastName}`
      : userDetails.email?.split('@')[0] || '';
    return comment.added_by_name === currentUserName || 
           comment.added_by_name === userDetails.email ||
           (userDetails.firstName && comment.added_by_name.includes(userDetails.firstName));
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin mb-3" />
        <p className="text-sm text-gray-600">{t('comments.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4">
        <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-3">
          <MessageSquare className="w-6 h-6 text-red-600" />
        </div>
        <p className="text-sm font-medium text-gray-900 mb-1">{t('comments.errorLoading')}</p>
        <p className="text-xs text-gray-500">{error}</p>
      </div>
    );
  }

  if (comments.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4">
        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <MessageSquare className="w-8 h-8 text-gray-400" />
        </div>
        <p className="text-sm font-medium text-gray-900 mb-1">{t('comments.noComments')}</p>
        <p className="text-xs text-gray-500 text-center max-w-xs">
          {t('comments.noCommentsDescription')}
        </p>
      </div>
    );
  }

  return (
    <div
      ref={scrollContainerRef}
      onScroll={handleScroll}
      className="flex-1 overflow-y-auto px-4 py-4 space-y-1"
      style={{ maxHeight: 'calc(100vh - 280px)' }}
    >
      {comments.map((comment) => (
        <CommentItem
          key={comment.id}
          comment={comment}
          isCurrentUser={isCurrentUserComment(comment)}
        />
      ))}
    </div>
  );
};

export default CommentsList;

